import { NEEDS_ATTENTION_BY_USER_KEYS } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-status';
import { Heading } from '@efdigital/ll-addon-ui';
import { OnboardingChecklist } from '@efdigital/ll-xceed-v2/components/coaching/coach/onboarding-checklist';
import { DashboardStat } from '@efdigital/ll-xceed-v2/components/coaching/dashboard-stat.gts';
import { QuickStartLink } from '@efdigital/ll-xceed-v2/components/coaching/quick-start-link.gts';
import { queryCoachingPartnershipsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-partnerships';
import { queryCoachingSessionsByCoach } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-sessions';
import type { CoachingCoachModel } from '@efdigital/ll-xceed-v2/routes/coaching/coach';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type SessionService from '@efdigital/ll-xceed-v2/services/session';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

interface IndexSignature {
  Args: {
    model: CoachingCoachModel;
    controller: unknown;
  };
}

const MAX = 20;

export default class CoachingCoachIndexComponent extends Component<IndexSignature> {
  @service declare coach: CoachService;
  @service declare store: Store;
  @service declare session: SessionService;

  async getActiveEngagements() {
    const engagements = await this.store.request(
      queryCoachingPartnershipsByCoach(
        this.session.cached.userId!,
        ['chosen'],
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(engagements.content.data.length, MAX),
      more: engagements.content.data.length > MAX,
    };
  }

  async getUpcomingSessions() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(this.session.cached.userId!, ['scheduled'], {
        offset: 0,
        limit: MAX + 1,
      })
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
      next: sessions.content.data[0],
    };
  }

  async getAttentionNeeded() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        NEEDS_ATTENTION_BY_USER_KEYS,
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
    };
  }

  async getSessionsDelivered() {
    const sessions = await this.store.request(
      queryCoachingSessionsByCoach(
        this.session.cached.userId!,
        ['attended', 'no_show_coachee'],
        { offset: 0, limit: MAX + 1 }
      )
    );
    return {
      count: Math.min(sessions.content.data.length, MAX),
      more: sessions.content.data.length > MAX,
    };
  }

  getStats = async () => {
    const [
      activeEngagements,
      upcomingSessions,
      attentionNeeded,
      sessionsDelivered,
    ] = await Promise.all([
      this.getActiveEngagements(),
      this.getUpcomingSessions(),
      this.getAttentionNeeded(),
      this.getSessionsDelivered(),
    ]);

    return {
      activeEngagements,
      upcomingSessions,
      attentionNeeded,
      sessionsDelivered,
    };
  };

  <template>
    <div class="container py-3">
      <Heading
        @size="lg"
        @text="Welcome, {{@model.coach.firstName}}"
        @secondary="Here's your coaching practice at a glance."
        @class="mb-4"
      />

      <section class="mb-4">
        <div class="row g-3">
          <div class="col-md-4">
            <QuickStartLink
              @route="coaching.coach.help.how-coaching-works"
              @icon="ph:lightbulb"
              @title="How coaching works here"
              @description="Learn about engagements, session credits, and chemistry calls."
              class="h-100"
            />
          </div>
          <div class="col-md-4">
            <QuickStartLink
              @route="coaching.coach.settings.index"
              @icon="ph:user-circle"
              @title="Complete your profile"
              @description="Add your bio, specialties, and credentials so coachees can find you."
              class="h-100"
            />
          </div>
          <div class="col-md-4">
            <QuickStartLink
              @route="index"
              @icon="ph:notebook"
              @title="Share Notes and Resources"
              @description="Share articles, templates, and files with coachees from your resource library."
              class="h-100"
            />
          </div>
        </div>
      </section>

      <OnboardingChecklist @coach={{@model.coach}} class="mb-4">
        <Await @promise={{(this.getStats)}}>
          <:success as |stats|>

            <div class="row mb-4">
              <div class="col-6 col-xl-3">
                <DashboardStat
                  @title="Active engagements"
                  @value={{stats.activeEngagements}}
                  @icon="ph:user"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-xl-3">
                <DashboardStat
                  @title="Upcoming sessions"
                  @value={{stats.upcomingSessions}}
                  @icon="ph:calendar"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-xl-3">
                <DashboardStat
                  @title="Attention needed"
                  @value={{stats.attentionNeeded}}
                  @icon="ph:warning"
                  class="h-100"
                />
              </div>
              <div class="col-6 col-xl-3">
                <DashboardStat
                  @title="Sessions delivered"
                  @value={{stats.sessionsDelivered}}
                  @icon="ph:credit-card"
                  class="h-100"
                />
              </div>
            </div>
          </:success>
        </Await>
      </OnboardingChecklist>
    </div>
  </template>
}
